/**
 * Auth Error Page Component
 * 
 * Displays authentication errors (e.g., user declined consent, callback failed).
 * Public page (no authentication required).
 */

import React from "react";
import { PublicLayout } from "../components/public/PublicLayout";
import { getQueryParam } from "../utils/urlParams";
import { login } from "../services/authApi";
import "./auth-bridge.css";

export function AuthErrorPage(): JSX.Element {
  const error = getQueryParam("error");
  
  // Note: Backend handles session/cookie cleanup on access_denied
  // Frontend just displays the error message

  const handleRetry = () => {
    // For rate limit, cooldown, or access_denied, redirect to login page (like Facebook)
    // This prevents loops and allows user to try different account
    const errorParam = getQueryParam("error");
    const detailParam = getQueryParam("detail");
    const errorDescription = getQueryParam("error_description");
    
    if (
      detailParam?.includes("Rate limit exceeded") || 
      errorParam === "rate_limit" ||
      errorDescription?.includes("Rate limit exceeded") ||
      window.location.href.includes("rate_limit") ||
      errorParam === "cooldown" ||
      errorParam === "access_denied" // Prevent loop after decline
    ) {
      // Rate limited, cooldown, or declined - redirect to login page so user can try different account
      // Don't call login() directly to avoid triggering rate limit again
      // Pass error info in URL so login page can show warning
      const errorType = errorParam === "rate_limit" ? "rate_limit" : errorParam === "cooldown" ? "cooldown" : "access_denied";
      window.location.href = `/auth/login?from_error=true&error_type=${errorType}`;
      return;
    }
    
    const returnTo = getQueryParam("returnTo") || "/";
    login(returnTo);
  };

  const getErrorMessage = () => {
    // Check if error is rate limit (from URL or error param)
    const errorParam = getQueryParam("error");
    const detailParam = getQueryParam("detail");
    const errorDescription = getQueryParam("error_description");
    
    // Check for rate limit in various formats
    if (
      detailParam?.includes("Rate limit exceeded") || 
      errorParam === "rate_limit" ||
      errorDescription?.includes("Rate limit exceeded") ||
      window.location.href.includes("rate_limit")
    ) {
      return {
        title: "Too Many Requests",
        message: "You've made too many login attempts. Please wait a few minutes before trying again. You can try logging in with a different account.",
      };
    }
    
    switch (error) {
      case "access_denied":
        return {
          title: "Authorization Declined",
          message: "You declined to authorize the application. Click 'Try Again' to go to the login page. When you click 'Sign In with Auth0', the login page will appear, allowing you to log in with a different account or accept the authorization request. If you decline again, you will be redirected back to this page.",
        };
      case "cooldown":
        const remaining = getQueryParam("remaining");
        return {
          title: "Please Wait",
          message: remaining 
            ? `Please wait ${remaining} seconds before trying again. You can try logging in with a different account.`
            : "Please wait a moment before trying again. You can try logging in with a different account.",
        };
      case "callback_failed":
        return {
          title: "Authentication Failed",
          message: "There was an error during authentication. Please try again.",
        };
      default:
        return {
          title: "Authentication Error",
          message: error 
            ? `An error occurred: ${error}` 
            : "An unknown error occurred during authentication. Please try again.",
        };
    }
  };

  const errorInfo = getErrorMessage();

  return (
    <PublicLayout>
      <div className="auth-bridge-page">
        <div className="auth-bridge-page__container">
          <div className="auth-bridge-page__error">
            <h1 className="auth-bridge-page__title">{errorInfo.title}</h1>
            <p className="auth-bridge-page__message">{errorInfo.message}</p>
            <div className="auth-bridge-page__actions">
              <button 
                onClick={handleRetry}
                className="auth-bridge-page__button auth-bridge-page__button--primary"
              >
                Try Again
              </button>
              <button 
                onClick={() => window.location.href = "/"}
                className="auth-bridge-page__button auth-bridge-page__button--secondary"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

export default AuthErrorPage;

